import { LivestreamSessionStreamDestinationRow } from 'cb-model';
import React, { useMemo, useState } from 'react';
import { Button, Card, Stack, Table } from 'react-bootstrap';
import { useStreamingDestinations } from '../hooks/useStreamingDestinations';
import { StreamingDestinationModal } from './StreamingDestinationModal';
import { Facebook, Youtube, Twitch, Link2 } from 'lucide-react';
type StreamPlatform = 'YOUTUBE' | 'FACEBOOK' | 'TWITCH' | 'CUSTOM';

const platformLabel: Record<StreamPlatform, string> = {
  YOUTUBE: 'YouTube',
  FACEBOOK: 'Facebook',
  TWITCH: 'Twitch',
  CUSTOM: 'Custom',
};

interface StreamDestinationsManagerProps {
  livestreamSessionId: string;
}

export function StreamingDestinations({ livestreamSessionId }: StreamDestinationsManagerProps) {
  const { loadingState, streamingDestinationsActions, isActionInProgress, streamingDestinations } =
    useStreamingDestinations(livestreamSessionId);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedDestination, setSelectedDestination] = useState<LivestreamSessionStreamDestinationRow | null>(null); // for toggling stream key reveal on mobile

  const anyEnabled = useMemo(() => streamingDestinations.some((d) => d.status === 'ACTIVE'), [streamingDestinations]);

  const onEdit = (id: string) => {
    const destination = streamingDestinations.find((d) => d.id === id);
    if (!destination) return;
    setSelectedDestination(destination);
    setShowAddModal(true);
  };

  const onAdd = () => {
    setSelectedDestination(null);
    setShowAddModal(true);
  };

  const onRemove = async (id: string) => {
    await streamingDestinationsActions.delete(id);
  };

  const onAddDestination = async (value: { platform: StreamPlatform; url: string; streamKey: string }) => {
    await streamingDestinationsActions.create(value.platform, value.streamKey, value.url);
    if (loadingState === 'IDLE') setShowAddModal(false);
  };

  const onUpdateDestination = async (
    id: string,
    value: { platform: StreamPlatform; url: string; streamKey: string },
  ) => {
    await streamingDestinationsActions.update(id, value.platform, value.streamKey, value.url);
    if (loadingState === 'IDLE') setShowAddModal(false);
  };

  // Shared small “icon” placeholder
  const PlatformIcon = ({ platform }: { platform: StreamPlatform }) => (
    <div
      className="rounded-2 border bg-white d-flex align-items-center justify-content-center flex-shrink-0"
      style={{ width: 34, height: 26 }}
      aria-hidden="true"
      title={platformLabel[platform]}
    >
      <span className="fw-semibold text-muted">
        {platform === 'YOUTUBE' ? (
          <Youtube size={16} />
        ) : platform === 'FACEBOOK' ? (
          <Facebook size={16} />
        ) : platform === 'TWITCH' ? (
          <Twitch size={16} />
        ) : (
          <Link2 size={16} />
        )}
      </span>
    </div>
  );

  return (
    <>
      <Card className="mt-3">
        <Card.Body>
          {/* Header */}
          <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-2">
            <div>
              <Card.Title className="mb-1">Streaming destinations (RTMP)</Card.Title>
              <Card.Text className="text-muted mb-0">
                Optional. Send the livestream to third party platforms like YouTube using RTMP.
              </Card.Text>
            </div>

            <Button variant="outline-secondary" size="sm" onClick={onAdd}>
              + Add destination
            </Button>
          </div>

          {/* Desktop/tablet: Table */}
          <div className="d-none d-lg-block mt-3 border rounded-3 overflow-hidden">
            <Table responsive className="mb-0 align-middle">
              <thead className="table-light">
                <tr>
                  <th>Platform</th>
                  <th style={{ width: 160 }}>Action</th>
                </tr>
              </thead>

              <tbody>
                {streamingDestinations.map((d) => {
                  return (
                    <tr key={d.id}>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <PlatformIcon platform={d.platform as StreamPlatform} />
                          <div className="fw-semibold">{platformLabel[d.platform as StreamPlatform]}</div>
                        </div>
                      </td>

                      <td>
                        <Stack direction="horizontal" gap={2}>
                          <Button size="sm" variant="primary" onClick={() => onEdit(d.id)}>
                            Edit
                          </Button>
                          <Button size="sm" variant="outline-secondary" onClick={() => onRemove(d.id)}>
                            Remove
                          </Button>
                        </Stack>
                      </td>
                    </tr>
                  );
                })}

                {streamingDestinations.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center text-muted py-4">
                      No destinations yet. Add one to start streaming to external platforms.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          {/* Mobile: Card list */}
          <div className="d-lg-none mt-3">
            <Stack gap={3}>
              {streamingDestinations.map((d) => {
                return (
                  <Card key={d.id} className="border shadow-sm">
                    <Card.Body>
                      {/* Top row */}
                      <div className="d-flex align-items-start justify-content-between gap-3">
                        <div className="d-flex align-items-center gap-2">
                          <PlatformIcon platform={d.platform as StreamPlatform} />
                          <div>
                            <div className="fw-semibold">{platformLabel[d.platform as StreamPlatform]}</div>
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="mt-3 d-flex flex-column flex-sm-row gap-2">
                        <Button variant="primary" size="sm" className="flex-fill" onClick={() => onEdit(d.id)}>
                          Edit
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="flex-fill"
                          onClick={() => onRemove(d.id)}
                        >
                          Remove
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                );
              })}

              {streamingDestinations.length === 0 && (
                <div className="text-center text-muted py-4 border rounded-3">
                  No destinations yet. Add one to start streaming to external platforms.
                </div>
              )}
            </Stack>
          </div>

          {/* Footer actions */}
          <div className="mt-3">
            {!anyEnabled && (
              <div className="text-muted small mt-2">Enable at least one destination to start streaming.</div>
            )}
          </div>
        </Card.Body>
      </Card>
      <StreamingDestinationModal
        show={showAddModal}
        mode={selectedDestination?.platform ? 'edit' : 'create'}
        onClose={() => setShowAddModal(false)}
        onSave={onAddDestination}
        onUpdate={onUpdateDestination}
        isProcessing={isActionInProgress}
        initialValue={
          selectedDestination
            ? {
                id: selectedDestination.id,
                platform: selectedDestination.platform as StreamPlatform,
                url: selectedDestination.url,
                streamKey: selectedDestination.stream_key,
              }
            : {}
        }
        error={loadingState === 'ERROR' ? 'Failed to add/update destination. Please try again.' : undefined}
      />
    </>
  );
}
